import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Tv, Radio, Clock, Bell, BellOff, Users, Calendar, Ticket, X, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { subscribePush, pushSupported } from "../lib/push";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import GCRAdBanner from "../components/GCRAdBanner";

function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function Live() {
  const nav = useNavigate();
  const [channels, setChannels] = useState([]);
  const [guides, setGuides] = useState({}); // {slug: [items]}
  const [reminders, setReminders] = useState([]); // schedule_ids the user has reminders on
  const [viewersBySlug, setViewersBySlug] = useState({}); // live viewer counts
  const [loading, setLoading] = useState(true);
  const [redeemOpen, setRedeemOpen] = useState(false);
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const refreshReminders = async () => {
    try { const { data } = await api.get("/reminders/mine"); setReminders(data || []); } catch (e) {
      console.warn("[live] reminders fetch failed", e?.response?.status);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: chs } = await api.get("/gcr/channels");
        if (cancelled) return;
        setChannels(chs);
        const entries = await Promise.all(chs.map(async (c) => {
          try {
            const { data } = await api.get(`/gcr/channels/${c.slug}/guide`, { params: { day: today } });
            return [c.slug, data.items];
          } catch { return [c.slug, []]; }
        }));
        if (!cancelled) setGuides(Object.fromEntries(entries));
      } finally { if (!cancelled) setLoading(false); }
      refreshReminders();
    })();
    return () => { cancelled = true; };
  }, [today]);

  // Poll live viewer counts for all channels every 15s
  useEffect(() => {
    if (channels.length === 0) return;
    let stopped = false;
    const tick = async () => {
      const results = await Promise.all(channels.map(async (c) => {
        try { const { data } = await api.get(`/gcr/channels/${c.slug}/viewers`); return [c.slug, data.viewers || 0]; }
        catch { return [c.slug, 0]; }
      }));
      if (!stopped) setViewersBySlug(Object.fromEntries(results));
    };
    tick();
    const t = setInterval(tick, 15000);
    return () => { stopped = true; clearInterval(t); };
  }, [channels]);

  const remIdFor = (sid) => reminders.find((r) => r.schedule_id === sid)?.reminder_id;

  const toggleReminder = async (sid, e) => {
    e.preventDefault(); e.stopPropagation();
    const existing = remIdFor(sid);
    try {
      if (existing) {
        await api.delete(`/reminders/${existing}`);
        toast.success("Reminder removed");
      } else {
        if (!pushSupported()) { toast.error("Notifications not supported in this browser"); return; }
        try { await subscribePush(); }
        catch (err) { toast.error(err.message || "Enable notifications first"); return; }
        await api.post("/reminders", { schedule_id: sid, minutes_before: 5 });
        toast.success("You'll be notified 5 min before this airs");
      }
      refreshReminders();
    } catch { toast.error("Reminder update failed"); }
  };

  // Hour columns 00:00 → 23:00
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const nowMs = Date.now();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <main className="pt-24 pb-24 px-6 lg:px-12 max-w-[1600px] mx-auto">
        <header className="mb-8">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="bg-[#E50914] p-2.5 rounded-lg"><Radio className="w-6 h-6" /></div>
            <div className="min-w-0 flex-1">
              <h1 className="text-3xl md:text-4xl font-black tracking-tight">GCR Theaters</h1>
              <p className="text-gray-400 text-sm mt-1">24/7 curated channels — playing live, right now.</p>
            </div>
            <button
              data-testid="open-showtimes-btn"
              onClick={() => nav("/live/showtimes")}
              className="bg-gradient-to-br from-amber-500 to-[#E50914] hover:brightness-110 text-white px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-lg"
            >
              <Calendar className="w-4 h-4" /> Showtimes & tickets
            </button>
            <button
              data-testid="open-redeem-btn"
              onClick={() => setRedeemOpen(true)}
              className="btn-glass px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 border border-white/15"
              title="Redeem a ticket access code"
            >
              <Ticket className="w-4 h-4" /> Redeem code
            </button>
          </div>
        </header>

        {/* Top sponsored banner — admin-managed */}
        <GCRAdBanner position="top" />

        {loading && (
          <div data-testid="live-loading" className="space-y-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="border border-white/5 rounded-lg p-5 bg-[#0f0f0f]">
                <div className="flex items-center gap-3 mb-4">
                  <div className="skeleton w-10 h-10 rounded-full" />
                  <div className="space-y-2">
                    <div className="skeleton h-4 w-32 rounded" />
                    <div className="skeleton h-3 w-44 rounded" />
                  </div>
                </div>
                <div className="flex gap-2 overflow-hidden">
                  {Array.from({ length: 6 }).map((__, j) => (
                    <div key={j} className="skeleton shrink-0 w-40 h-20 rounded" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && channels.length === 0 && (
          <div data-testid="live-empty" className="bg-[#0f0f0f] border border-white/5 rounded-lg p-10 text-center">
            <Tv className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No channels available in your region</h2>
            <p className="text-gray-400 text-sm">Check back later or contact support.</p>
          </div>
        )}

        {!loading && channels.length > 0 && (
          <div data-testid="live-guide" className="bg-[#0c0c0c] border border-white/5 rounded-lg overflow-hidden">
            {/* Hour ruler */}
            <div className="grid sticky top-0 z-10 bg-[#0a0a0a] border-b border-white/10" style={{ gridTemplateColumns: "180px repeat(24, minmax(80px, 1fr))" }}>
              <div className="px-4 py-3 text-xs uppercase tracking-widest text-gray-500 border-r border-white/10">Channel</div>
              {hours.map((h) => (
                <div key={h} className="px-2 py-3 text-xs text-gray-500 text-center border-r border-white/5 last:border-0">
                  {h.toString().padStart(2, "0")}:00
                </div>
              ))}
            </div>

            {/* Channel rows */}
            {channels.map((c) => {
              const items = guides[c.slug] || [];
              return (
                <div
                  key={c.slug}
                  data-testid={`channel-row-${c.slug}`}
                  className="grid border-b border-white/5 last:border-0 hover:bg-white/[0.015] transition-colors relative"
                  style={{ gridTemplateColumns: "180px repeat(24, minmax(80px, 1fr))" }}
                >
                  <button
                    data-testid={`channel-tune-${c.slug}`}
                    onClick={() => nav(`/live/${c.slug}`)}
                    className="px-4 py-4 border-r border-white/10 flex items-center gap-3 text-left hover:bg-white/5"
                    style={{ borderLeft: `3px solid ${c.color || "#E50914"}` }}
                  >
                    {c.logo ? (
                      <img src={c.logo} alt="" className="w-10 h-10 object-cover rounded" />
                    ) : (
                      <div className="w-10 h-10 rounded flex items-center justify-center font-black" style={{ background: c.color || "#E50914" }}>
                        {c.name?.[0] || "?"}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">{c.name}</div>
                      <div className="text-[11px] text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                        <span>Live</span>
                        {(viewersBySlug[c.slug] || 0) > 0 && (
                          <span
                            data-testid={`channel-viewers-${c.slug}`}
                            className="inline-flex items-center gap-0.5 text-[#E50914] normal-case tracking-normal font-semibold"
                            title={`${viewersBySlug[c.slug]} watching now`}
                          >
                            <Users className="w-3 h-3" />{viewersBySlug[c.slug]}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>

                  {/* Schedule blocks (absolute over the 24-hour grid) */}
                  <div className="col-span-24 relative h-20" style={{ gridColumn: "2 / span 24" }}>
                    {items.map((it) => {
                      const sMs = new Date(it.starts_at).getTime();
                      const eMs = new Date(it.ends_at).getTime();
                      const dayStart = new Date(today + "T00:00:00Z").getTime();
                      const totalMs = 24 * 3600 * 1000;
                      const left = Math.max(0, ((sMs - dayStart) / totalMs) * 100);
                      const width = Math.max(0.5, ((eMs - sMs) / totalMs) * 100);
                      const isLive = nowMs >= sMs && nowMs < eMs;
                      const hasReminder = !!remIdFor(it.schedule_id);
                      const isFuture = nowMs < sMs;
                      return (
                        <button
                          key={it.schedule_id}
                          data-testid={`block-${it.schedule_id}`}
                          onClick={() => nav(`/live/${c.slug}`)}
                          className={`absolute top-1 bottom-1 px-2 py-1.5 rounded text-left overflow-hidden whitespace-nowrap text-ellipsis text-xs border ${
                            isLive
                              ? "bg-[#E50914]/15 border-[#E50914]/60 ring-1 ring-[#E50914]/40"
                              : "bg-white/[0.03] border-white/10 hover:bg-white/[0.07]"
                          }`}
                          style={{ left: `${left}%`, width: `${width}%` }}
                          title={`${it.title} · ${fmtTime(it.starts_at)}–${fmtTime(it.ends_at)}`}
                        >
                          {isLive && <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#E50914] mr-1.5 animate-pulse" />}
                          <span className="font-semibold">{it.title}</span>
                          <span className="text-gray-400 ml-1">{fmtTime(it.starts_at)}</span>
                          {isFuture && (
                            <span
                              data-testid={`reminder-${it.schedule_id}`}
                              role="button"
                              tabIndex={0}
                              onClick={(e) => toggleReminder(it.schedule_id, e)}
                              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggleReminder(it.schedule_id, e); }}
                              className={`absolute top-1 right-1 p-1 rounded transition-colors ${hasReminder ? "text-[#E50914]" : "text-gray-500 hover:text-white"}`}
                              aria-label={hasReminder ? "Remove reminder" : "Set reminder"}
                            >
                              {hasReminder ? <Bell className="w-3 h-3 fill-current" /> : <BellOff className="w-3 h-3" />}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <p className="text-xs text-gray-500 px-4 py-3 flex items-center gap-2">
              <Clock className="w-3.5 h-3.5" /> Times shown are UTC. Click any channel or program to tune in.
            </p>
          </div>
        )}

        {/* Bottom sponsored banner — admin-managed */}
        <div className="mt-8">
          <GCRAdBanner position="bottom" />
        </div>
      </main>
      {redeemOpen && <RedeemModal onClose={() => setRedeemOpen(false)} />}
      <Footer />
    </div>
  );
}

function RedeemModal({ onClose }) {
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const c = code.trim();
    if (!c) { toast.error("Enter your access code"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/gcr/tickets/redeem", { access_code: c });
      setResult(data);
      if (data.redeemed) toast.success("Ticket claimed!");
      else if (data.already_yours) toast.info("This code already belongs to you — tune in below.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not redeem code");
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="redeem-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm px-4"
      onClick={() => !busy && onClose()}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-gradient-to-br from-[#0a0a0a] to-[#1a0a0a] border border-[#E50914]/40 rounded-xl p-6 shadow-2xl"
      >
        <button type="button" onClick={() => !busy && onClose()} disabled={busy} className="absolute top-3 right-3 text-gray-400 hover:text-white" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 mb-2">
          <Ticket className="w-6 h-6 text-[#E50914]" />
          <h3 className="text-xl font-black">Redeem access code</h3>
        </div>
        <p className="text-sm text-gray-400 mb-5">
          Enter the code from your ticket email or from a friend who booked a group. We accept lowercase, with or without dashes.
        </p>

        {!result && (
          <>
            <label className="block text-xs uppercase tracking-widest text-gray-400 mb-1">Access code</label>
            <input
              data-testid="redeem-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 32))}
              placeholder="TKT-XXXX-XXXX"
              autoFocus
              autoComplete="off"
              className="w-full bg-black/60 border border-white/15 focus:border-[#E50914] outline-none rounded px-3 py-3 text-center font-mono text-lg tracking-wider"
              disabled={busy}
            />
            <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-white/10">
              <button type="button" onClick={onClose} disabled={busy} className="btn-glass px-5 py-2.5 rounded text-sm">Cancel</button>
              <button
                data-testid="redeem-submit"
                type="submit"
                disabled={busy || code.length < 4}
                className="btn-red px-6 py-2.5 rounded text-sm font-bold flex items-center gap-2 disabled:opacity-50"
              >
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</> : <><Ticket className="w-4 h-4" /> Redeem</>}
              </button>
            </div>
          </>
        )}

        {result && (
          <div data-testid="redeem-result" className="text-center py-2">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/40 mb-3">
              <Ticket className="w-7 h-7 text-emerald-400" />
            </div>
            <h4 className="text-2xl font-black mb-1">
              {result.already_yours ? "Already yours" : "Ticket claimed!"}
            </h4>
            <p className="text-sm text-gray-400 mb-1">{result.video?.title}</p>
            <p className="text-xs text-gray-500 mb-5">
              {result.channel?.name} · {result.schedule?.start_time} UTC
            </p>
            <div className="bg-black/40 rounded px-3 py-2 mb-5 font-mono text-sm text-amber-300 tracking-wider">
              {result.access_code}
            </div>
            <button
              type="button"
              data-testid="redeem-go-channel"
              onClick={() => { nav(`/live/${result.channel?.slug}`); onClose(); }}
              className="btn-red w-full px-5 py-2.5 rounded text-sm font-bold flex items-center justify-center gap-2"
            >
              <Radio className="w-4 h-4" /> Go to {result.channel?.name}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

